import React, { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import CardsResumoRelatorios from './CardsResumoRelatorios';
import TabelaRelatorios from './TabelaRelatorios';
import ModalTransacoesDiarias from './ModalTransacoesDiarias';
import SeletorPeriodo from './SeletorPeriodo';
import api from 'services/api';

const AnaliseRelatorios = ({
  dados,
  dataInicio,
  dataFim,
  onDataInicioChange,
  onDataFimChange,
  showHeader = true,
  withContainer = true
}) => {
  const [modalAberto, setModalAberto] = useState(false);
  const [transacoes, setTransacoes] = useState([]);
  const [dataSelecionada, setDataSelecionada] = useState(null);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [loadingTransacoes, setLoadingTransacoes] = useState(false);

  // Processar dados agrupados por conta bancária
  const dadosProcessados = useMemo(() => {
    if (!dados || dados.length === 0) return [];

    // Os dados já vêm processados por conta bancária do Relatórios.jsx
    // Apenas precisamos garantir que estão no formato correto para os componentes
    return dados.map(item => ({
      nome: item.conta || item.conta_bancaria || 'Não informado',
      totalContas: item.totalRegistros || 0,
      saldo: item.saldo || item.valor_total || item.valor || 0,
      saldo_inicial: item.saldo_inicial || item.saldoInicial || 0,
      valorTotal: item.saldo || item.valor_total || item.valor || 0, // compatibilidade
      entrada: item.entrada || item.valor_pago || 0,
      valorPago: item.entrada || item.valor_pago || 0, // compatibilidade
      saida: item.saida || item.valor_aberto || 0,
      valorAberto: item.saida || item.valor_aberto || 0, // compatibilidade
      contasVencidas: item.contasVencidas || 0,
      proximasVencimento: item.proximasVencimento || 0,
      fornecedoresUnicos: item.fornecedoresUnicos || 0,
      historico: item.historico || [],
      dadosConta: item.dadosConta || {},
      itens: [item]
    })).sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo));
  }, [dados]);

  const emptyState = (
    <div className="bg-[color:var(--surface)] rounded-lg p-6 border border-[color:var(--border)]">
      <div className="text-center text-[color:var(--text-muted)]">
        <FileText className="h-12 w-12 mx-auto mb-4 text-[color:var(--text-muted)]" />
        <p>Nenhum dado encontrado para análise</p>
      </div>
    </div>
  );

  if (!dados || dados.length === 0) {
    return withContainer ? emptyState : <div className="py-6">{emptyState}</div>;
  }

  // Handler para buscar transações diárias
  const handleDiaClick = async (data) => {
    try {
      // Abrir modal imediatamente e mostrar loading dentro dele
      setModalAberto(true);
      setLoadingTransacoes(true);
      setDataSelecionada(data.data);
      setContaSelecionada(data.conta);
      setTransacoes([]); // Limpar transações anteriores

      // Extrair id_conta dos dados da conta
      // Prioridade: transação.conta > dadosConta.id > dadosConta.ID > conta.id
      const idConta = data.transacao?.conta || 
                      data.conta.dadosConta?.id || 
                      data.conta.dadosConta?.ID || 
                      data.conta.id ||
                      data.conta.itens?.[0]?.dadosConta?.id;

      // Extrair id_planejamento dos dados da conta (se existir)
      const idPlanejamento = data.conta.dadosConta?.id_planejamento || 
                             data.conta.dadosConta?.idPlanejamento ||
                             data.conta.id_planejamento ||
                             data.conta.idPlanejamento ||
                             data.conta.itens?.[0]?.dadosConta?.id_planejamento ||
                             null;

      if (!idConta) {
        console.error('ID da conta não encontrado:', data);
        alert('Erro: ID da conta não encontrado');
        setLoadingTransacoes(false);
        setModalAberto(false);
        return;
      }

      // Formatar data para YYYY-MM-DD se necessário
      let dataFormatada = data.data;
      if (dataFormatada && dataFormatada.includes('/')) {
        // Converter DD/MM/YYYY para YYYY-MM-DD
        const partes = dataFormatada.split('/');
        if (partes.length === 3) {
          dataFormatada = `${partes[2]}-${partes[1]}-${partes[0]}`;
        }
      } else if (dataFormatada && !dataFormatada.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Se não está no formato ISO, tentar converter
        try {
          const dateObj = new Date(dataFormatada);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            dataFormatada = `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error('Erro ao formatar data:', e);
        }
      }

      // Preparar payload
      const payload = {
        data: dataFormatada,
        id_conta: idConta
      };

      // Adicionar id_planejamento ao payload se existir
      if (idPlanejamento !== null) {
        payload.id_planejamento = idPlanejamento;
      }

      const response = await api.post('/n8n/administracao/relatorios/transacoes-diarias', payload);

      if (response.data.success) {
        setTransacoes(response.data.data || []);
      } else {
        console.error('Erro ao buscar transações:', response.data.message);
        setTransacoes([]);
      }
    } catch (error) {
      console.error('Erro ao buscar transações diárias:', error);
      setTransacoes([]);
    } finally {
      setLoadingTransacoes(false);
    }
  };

  const fecharModal = () => {
    setModalAberto(false);
    setTransacoes([]);
    setDataSelecionada(null);
    setContaSelecionada(null);
  };

  const content = (
    <>
      {showHeader && (
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-6 w-6 text-[color:var(--accent)]" />
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">Análise de Relatórios</h3>
          </div>

          {dataInicio && dataFim && (
            <div className="bg-[color:var(--surface-muted)] rounded-lg p-4 border border-[color:var(--border)]">
              <SeletorPeriodo
                dataInicio={dataInicio}
                dataFim={dataFim}
                onDataInicioChange={onDataInicioChange}
                onDataFimChange={onDataFimChange}
              />
            </div>
          )}
        </div>
      )}

      <div className="space-y-6">
        <CardsResumoRelatorios dados={dadosProcessados} />
        <TabelaRelatorios
          dados={dadosProcessados}
          onDiaClick={handleDiaClick}
        />
      </div>

      <ModalTransacoesDiarias
        isOpen={modalAberto}
        onClose={fecharModal}
        transacoes={transacoes}
        data={dataSelecionada}
        contaNome={contaSelecionada?.nome || 'N/A'}
        loading={loadingTransacoes}
      />
    </>
  );

  if (!withContainer) {
    return <div className="space-y-6">{content}</div>;
  }

  return (
    <div className="bg-[color:var(--surface)] rounded-lg p-4 sm:p-6 border border-[color:var(--border)]">
      {content}
    </div>
  );
};

export default AnaliseRelatorios;
